import { Location } from '@angular/common';
import { Component, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, ArkUnitOfWork, AuthService } from 'app/core/core.module';
import { Campaign, CampaignAction, CampaignOptOut, CommitteeAttachment, Person } from 'app/core/model/entity-model';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { DxDataGridComponent, DxDataGridModule, DxDateBoxModule, DxFormModule } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import { BsModalService } from 'ngx-bootstrap/modal';
import { CampaignDialogComponent } from './campaign-dialog.component';
import { default as validationEngine } from 'devextreme/ui/validation_engine';

@Component({
    standalone: true,
    imports: [DxDataGridModule, DxFormModule, DxDateBoxModule],
    selector: 'app-campaign-detail',
    template: `
        <div class="btn-toolbar mx-1 mt-1 nomination-toolbar">
            <div class="d-flex flex-fill">
                @if (hasChanges()) {
                    <button class="btn btn-sm btn-primary me-2" (click)="save()">Save</button>
                    <button class="btn btn-sm btn-success me-4" (click)="cancel()">Cancel</button>
                }
                @if (!hasChanges()) {
                    <button class="btn btn-sm btn-primary me-4" (click)="close()">Close</button>
                }
            </div>
        </div>

        <dx-form [formData]="campaign" class="m-1" validationGroup="validationGroup">
            <dxi-item dataField="name" caption="Name" [editorOptions]="{ width: '350px' }">
                <dxi-validation-rule type="required" message="Name is required"></dxi-validation-rule>
            </dxi-item>
            <dxi-item dataField="startDate" caption="Start Date" editorType="dxDateBox" [editorOptions]="{ width: '150px' }"></dxi-item>
        </dx-form>
        <div class="d-flex flex-row mt-3 h-100 gap-3" style="width: 75%">
            <dx-data-grid
                #dataGrid
                style="flex: 2"
                keyExpr="id"
                height="100%"
                [dataSource]="actions"
                [showBorders]="true"
                [columnAutoWidth]="false"
                [focusedRowEnabled]="false"
                [showColumnLines]="true"
                [wordWrapEnabled]="true"
                (onToolbarPreparing)="onToolbarPreparing($event)"
                (onSelectionChanged)="onSelectionChanged($event)"
                (onCellDblClick)="rowDoubleClick($event)"
                (onRowPrepared)="rowPrepare($event)"
            >
                <dxi-column dataField="date" caption="Date" width="150" [sortIndex]="1" [sortOrder]="'asc'" [format]="'EEEE, MMMM dd'"></dxi-column>
                <dxi-column dataField="name" caption="Message"></dxi-column>
                <dxi-column width="50"></dxi-column>

                <dxo-filter-row [visible]="true"></dxo-filter-row>
                <dxo-search-panel [visible]="true"></dxo-search-panel>
                <dxo-selection [mode]="'multiple'" [showCheckBoxesMode]="'onClick'"></dxo-selection>
                <div *dxTemplate="let data of 'toolbarButtons'">
                    <button class="btn btn-sm btn-primary ms-1" (click)="addEditAction()">Add</button>
                    <button
                        class="btn btn-sm btn-success ms-1"
                        (click)="addEditAction(dataGrid.instance.getSelectedRowsData()[0])"
                        [disabled]="dataGrid.instance.getSelectedRowsData().length !== 1"
                    >
                        Edit
                    </button>
                    <button class="btn btn-sm btn-danger ms-2" (click)="deleteAction()" [disabled]="dataGrid.instance.getSelectedRowsData().length !== 1">Delete</button>
                </div>
                <div *dxTemplate="let data of 'toolbarText'"></div>
            </dx-data-grid>
            <dx-data-grid
                #optOutGrid
                style="flex: 1"
                keyExpr="id"
                height="100%"
                [dataSource]="optOuts"
                [showBorders]="true"
                [columnAutoWidth]="false"
                [focusedRowEnabled]="false"
                [showColumnLines]="true"
                [wordWrapEnabled]="true"
                (onToolbarPreparing)="onToolbarPreparing($event)"
                (onSelectionChanged)="onSelectionChanged($event)"
            >
                <dxi-column dataField="person.displayName" caption="Name" width="220"></dxi-column>
                <dxi-column dataField="person.cellPhone" caption="Cell Phone" width="220"></dxi-column>
                <dxi-column width="20"></dxi-column>
                <dxo-filter-row [visible]="true"></dxo-filter-row>
                <dxo-search-panel [visible]="true"></dxo-search-panel>
                <dxo-selection [mode]="'multiple'" [showCheckBoxesMode]="'onClick'"></dxo-selection>
                <div *dxTemplate="let data of 'toolbarButtons'">
                    <button class="btn btn-sm btn-primary ms-1" (click)="addOptOut()">Add</button>
                    <button class="btn btn-sm btn-danger ms-2" (click)="deleteOptOut()" [disabled]="optOutGrid.instance.getSelectedRowsData().length !== 1">Delete</button>
                </div>
                <div *dxTemplate="let data of 'toolbarText'">
                    @if (optOuts) {
                        <span class="me-3">
                            <strong>{{ optOutGrid.instance.totalCount() + ' items' }}</strong>
                        </span>
                    }
                </div>
            </dx-data-grid>
        </div>
    `,
    styles: ``,
})
export class CampaignDetailComponent implements OnInit {
    campaigns: Campaign[] = [];
    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    @ViewChild('optOutGrid') optOutGrid: DxDataGridComponent;
    router = inject(Router);
    route = inject(ActivatedRoute);

    key: any;
    campaign: any;
    actions: CampaignAction[] = [];
    optOuts: CampaignOptOut[] = [];
    people: Person[] = [];

    validationGroup = 'validationGroup';
    errorAction = 'saving';

    constructor(
        private uow: ArkUnitOfWork,
        private auth: AuthService,
        private modalService: BsModalService,
        public appInfo: AppInformationService,
        protected location: Location,
    ) {}

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate() {
        return !this.hasChanges();
    }
    hasChanges() {
        return this.uow.hasChanges();
    }
    async ngOnInit() {
        this.key = this.route.snapshot.params['id'];

        if (this.key === 'new') {
            this.campaign = this.uow.campaigns.createEntity({
                created: new Date(),
                startDate: new Date(),
                createdBy: this.auth.currentUser.userName,
            });
        } else {
            await this.getData();
        }
    }

    async getData() {
        this.appInfo.pleaseWaitShow();
        this.campaign = await this.uow.campaigns.byId(this.key, 'actions,optOuts.person');
        this.actions = this.campaign.actions.filter((x) => !x.deleted) || [];
        this.optOuts = this.campaign.optOuts || [];
        this.appInfo.pleaseWaitHide();
    }

    onSelectionChanged(e) {
        const rows = this.dataGrid.instance.getSelectedRowsData();
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    async addEditAction(selectedRow?: any) {
        const modalRef = this.modalService.show(CampaignDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                campaignId: this.campaign.id,
                campaignAction: selectedRow ? selectedRow : null,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
        });
    }

    async addOptOut() {
        const people = (await this.uow.people.all()).map((x) => {
            return {
                id: x.id,
                name: `${x.firstName} ${x.lastName}`,
            };
        });

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Add Person',
                dialogPrompt: 'Select Person',
                dialogValueArray: people,
                mode: 'selectWithId',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                const optOut = this.uow.campaignOptOuts.createEntity({
                    campaignId: this.campaign.id,
                    personId: result.value,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                });
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
        });
    }

    rowDoubleClick(e) {
        this.addEditAction(e.data);
    }

    attachmentDoubleClick(e) {
        this.openAttachment(e.data);
    }

    openAttachment(attachment: CommitteeAttachment) {
        if (!attachment) return;
        window.open(attachment.filename, '_blank');
    }

    rowPrepare(e) {
        if (e.rowType === 'data') {
            if (!e.data.approved && !e.data.declined) {
                e.rowElement.className = e.rowElement.className + ' waiting-for-vote';
            }
        }
    }

    create() {}

    open(campaign?: any) {
        const campaignId = campaign?.id ?? campaign?.Id;
        if (!campaignId) this.router.navigate(['/campaigns/new']);
        else this.router.navigate(['/campaigns', campaignId]);
    }

    async add(selectedRow?: any) {
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'text',
                dialogHeader: 'New Campaign',
                dialogValue: '',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                const campaign = this.uow.campaigns.createEntity({
                    name: result.value,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                    startDate: new Date(),
                });
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
        });
    }

    async deleteAction() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const result = await confirm(`Are you sure you want to delete?`, `GRRAFSCHAP`);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        selectedRows.forEach((row) => {
            row.deleted = new Date();
            row.deletedBy = this.auth.currentUser.userName;
        });
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async deleteOptOut() {
        const selectedRows = this.optOutGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const result = await confirm(`Are you sure you want to delete?`, `GRRAFSCHAP`);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        selectedRows.forEach((row) => {
            row.entityAspect.setDeleted();
        });
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async save() {
        const result = this.validate();
        if (!result) return false;

        this.appInfo.pleaseWaitShow();

        if (this.key === 'new') {
            await this.uow.commit();
            this.key = this.campaign.id;
            this.router.navigate(['/messages/campaigns/' + this.campaign.id], { replaceUrl: true });
        } else await this.uow.commit();

        await this.getData();
        this.appInfo.pleaseWaitHide();
        return true;
    }

    cancel() {
        this.uow.rollback();
        this.close();
    }

    close() {
        try {
            this.location.back();
        } catch {
            this.router.navigate(['../'], { relativeTo: this.route });
        }
    }

    validate() {
        const groupConfig = validationEngine.getGroupConfig(this.validationGroup);
        if (groupConfig) {
            const result = validationEngine.validateGroup(this.validationGroup);
            if (!result.isValid) {
                let message = '';
                result.brokenRules.forEach((brokenRule) => {
                    if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
                });
                this.appInfo.notifyToast(`Please fix errors before ${this.errorAction}.\n\n${message}`, 'error');
                return false;
            }
        }
        return true;
    }
}
